import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Home, Shield, Users, Heart, Eye, EyeOff } from 'lucide-react';
import ShelterCareConnectLogo from './ui/logo';
import { toast } from 'sonner';

const Login = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  if (user) {
    return <Navigate to="/dashboard" />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Trim stray whitespace (common cause of "invalid password" from copy/paste or mobile autofill)
    const username = credentials.username.trim();
    const password = credentials.password.trim();

    const result = await login(username, password);
    
    if (result.success) {
      toast.success('Welcome to Shelter Care Connect!');
      navigate('/dashboard'); // Navigate to dashboard after login
    } else {
      setError(result.error);
      toast.error(result.error);
    }
    
    setLoading(false);
  };

  const handleChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-8">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="flex justify-center mb-4">
            <ShelterCareConnectLogo size="lg" showText={true} />
          </div>
          <p className="text-gray-600">Comprehensive Shelter Management System</p>
          
          {/* Features highlights */}
          <div className="grid grid-cols-3 gap-4 mt-6 text-center">
            <div className="flex flex-col items-center space-y-2">
              <Users className="h-6 w-6 text-indigo-600" />
              <span className="text-xs text-gray-600">Resident Management</span>
            </div>
            <div className="flex flex-col items-center space-y-2">
              <Shield className="h-6 w-6 text-indigo-600" />
              <span className="text-xs text-gray-600">Secure & Private</span>
            </div>
            <div className="flex flex-col items-center space-y-2">
              <Heart className="h-6 w-6 text-indigo-600" />
              <span className="text-xs text-gray-600">Easy to Use</span>
            </div>
          </div>
        </div>

        {/* Login Form */}
        <Card className="card-shadow-lg border-0 bg-white/80 backdrop-blur-sm">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <ShelterCareConnectLogo size="lg" showText={true} />
            </div>
            <CardDescription>Sign in to your account</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert className="border-red-200 bg-red-50">
                  <AlertDescription className="text-red-700">
                    {error}
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <label htmlFor="username" className="text-sm font-medium text-gray-700">
                  Username
                </label>
                <Input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  required
                  value={credentials.username}
                  onChange={handleChange}
                  placeholder="Enter your username"
                  className="focus-ring"
                  data-testid="login-username-input"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={credentials.password}
                    onChange={handleChange}
                    placeholder="Enter your password"
                    className="focus-ring pr-10"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2.5"
                data-testid="login-submit-button"
              >
                {loading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Signing in...</span>
                  </div>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            {/* Demo credentials */}
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="text-sm font-medium text-blue-900 mb-2">Demo Credentials:</h4>
              <p className="text-xs text-blue-700">
                <strong>Username:</strong> admin<br />
                <strong>Password:</strong> admin123
              </p>
              <p className="text-xs text-blue-600 mt-2">
                Use these credentials to explore the system
              </p>
            </div>

            <div className="mt-4 text-center text-sm text-gray-600">
              New shelter?{' '}
              <button
                type="button"
                onClick={() => navigate('/signup')}
                className="text-indigo-600 font-medium hover:underline"
                data-testid="go-to-signup-link"
              >
                Create a free account
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-gray-500">
          <p>Secure • Reliable • User-Friendly</p>
          <p className="mt-1">Built specifically for homeless shelter operations</p>
        </div>
      </div>
    </div>
  );
};

export default Login;